// FILE: src/pages/SearchResults.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSearchClient from '../hooks/useSearchClient';
import SearchInput from '../components/search/SearchInput';
import SearchResultsList from '../components/search/SearchResultsList';
import { trackSearchSelect } from '../utils/analytics';
import { errorDev } from '../utils/logger';

export default function SearchResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialQuery = params.get('q') || '';
  const initialCat = params.get('cat') || 'all';

  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState(initialCat);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const { search, prefetchIndex } = useSearchClient();

  // Combobox state
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    prefetchIndex().catch(() => {});
  }, [prefetchIndex]);

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    const nextQuery = nextParams.get('q') || '';
    const nextCat = nextParams.get('cat') || 'all';
    setQuery(nextQuery);
    setCategory(nextCat);
  }, [location.search]);

  useEffect(() => {
    let active = true;
    if (!query) {
      setResults([]);
      setActiveIndex(-1);
      return;
    }
    setLoading(true);
    setError(null);
    search(query, { category, limit: 80 })
      .then((res) => {
        if (!active) return;
        setResults(res || []);
        setActiveIndex(res && res.length ? 0 : -1);
      })
      .catch((err) => {
        errorDev('Search error', err);
        if (!active) return;
        setError('Search failed');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => (active = false);
  }, [query, category, search]);

  // Scroll the active result into view: SearchResultsList handles this internally,
  // but we still keep the activeIndex here and sync.
  const handleSelect = (item) => {
    if (!item) return;
    try {
      trackSearchSelect(item);
    } catch (e) {}
    if (item.path) navigate(item.path);
    else if (item.action) {
      if (typeof item.action === 'string' && item.action.startsWith('/')) navigate(item.action);
      else if (typeof item.action === 'function') item.action();
    }
  };

  const handleInputKeyDown = (e) => {
    if (!results || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((s) => Math.min((s < 0 ? -1 : s) + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((s) => Math.max((s < 0 ? results.length : s) - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        handleSelect(results[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (query) {
        setQuery('');
      } else {
        // go back to previous page
        try {
          navigate(-1);
        } catch (err) {
          errorDev("Operation failed:", err);
          navigate('/north-star');
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 text-black dark:text-white">
      <div className="max-w-4xl mx-auto p-4">
        <div className="mb-3">
          <select
            value={category || 'all'}
            onChange={(e) => {
              const nextCat = e.target.value;
              setCategory(nextCat);
              const next = new URLSearchParams(location.search);
              if (nextCat && nextCat !== 'all') next.set('cat', nextCat);
              else next.delete('cat');
              navigate(`/search?${next.toString()}`, { replace: true });
            }}
            className="h-10 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 text-sm"
            aria-label="Search catalogue"
          >
            <option value="all">Catalogue: All</option>
            <option value="farm">Catalogue: Farm</option>
            <option value="farmpulse">Catalogue: FarmPulse</option>
            <option value="farmmark">Catalogue: FarmMark</option>
            <option value="northstar">Catalogue: NorthStar</option>
            <option value="platform">Catalogue: Our Platform</option>
          </select>
        </div>

        <SearchInput
          ref={inputRef}
          value={query}
          onChange={setQuery}
          onKeyDown={handleInputKeyDown}
          controlsId="search-results-list"
          expanded={results && results.length > 0}
        />

        <div className="mt-4">
          {loading && <div className="text-sm">Searching…</div>}
          {error && <div className="text-sm text-red-500">{error}</div>}
          {!loading && !error && (
            <SearchResultsList
              results={results}
              onSelect={handleSelect}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
            />
          )}
        </div>
      </div>
    </div>
  );
}
